/**
 * Collection store — leaf module (Perf 3.1.b Step 4).
 * Cross-store refs (auth/chat) via runtime getStores() so the barrel (which
 * re-exports this leaf) is not statically imported back (circular init).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { collectionsApi } from '@/lib/api/collections';
import { getStorage } from './storage';
import { FILTER_CONFIGS } from './filterConfigs';

const getStores = () => require('@/store');

const useCollectionStore = create(
  persist(
  (set, get) => ({
  _hasHydrated: false,
  // State
  collections: {}, // Map of collections by id
  collectionIds: [], // Array of collection ids for listing
  isLoading: {
    fetch: false,
    create: false,
    update: false,
    addConversation: false,
    removeConversation: false,
    addFiles: false,
    removeFile: false,
    delete: false
  },
  errors: {
    fetch: null,
    create: null,
    update: null,
    addConversation: null,
    removeConversation: null,
    addFiles: null,
    removeFile: null,
    delete: null
  },
  sortOrder: 'newest',
  contentSortOrders: {}, // Per-collection chat and file sort preferences
  pagination: {
    page: 1,
    pages: 1,
    total: 0,
    perPage: 20,
    hasMore: false
  },

  setPagination: (pagination) => set({ pagination }),

  setSortOrder: (order) => set({ sortOrder: order }),

  setContentSortOrder: (collectionId, contentType, order) => {
    if (!collectionId || !['chats', 'files'].includes(contentType)) return;
    if (!FILTER_CONFIGS.collections.sort.some(option => option.id === order)) return;

    set(state => ({
      contentSortOrders: {
        ...state.contentSortOrders,
        [collectionId]: {
          ...state.contentSortOrders[collectionId],
          [contentType]: order,
        },
      },
    }));
  },

  handleFilterSortClick: (labelId) => {
    if (!labelId) {
      set({ sortOrder: null });
      return;
    }

    const label = FILTER_CONFIGS.collections.sort
      .find(l => l.id === labelId);
    
    if (!label) return;

    set({ sortOrder: labelId });
  },

  fetchCollections: async (page = 1) => {
    if (!getStores().useAuthStore.getState().token) {
      set(state => ({
        isLoading: { ...state.isLoading, fetch: false },
        errors: { ...state.errors, fetch: null },
      }));
      return [];
    }
    try {      
      set(state => ({ 
        isLoading: { ...state.isLoading, fetch: true },
        errors: { ...state.errors, fetch: null }
      }));
      
      const { collections, pagination } = await collectionsApi.fetch(page);
      
      // Use addCollections to add the fetched collections
      const addedCollections = get().addCollections(collections);
      
      // Update pagination only (addCollections handles collectionIds)
      set(state => ({ 
        pagination,
        isLoading: { ...state.isLoading, fetch: false }
      }));

      return collections;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, fetch: error.message || 'Failed to fetch collections' },
        isLoading: { ...state.isLoading, fetch: false }
      }));
      // throw error;
    }
  },

  fetchCollectionById: async (id, force = false) => {
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, fetch: true },
        errors: { ...state.errors, fetch: null }
      }));
      
      // Check if we already have this collection in the store
      const existingCollection = get().collections[id];
      if (existingCollection && !force) {
        set(state => ({ isLoading: { ...state.isLoading, fetch: false } }));
        return existingCollection;
      }

      const collection = await collectionsApi.getById(id);
      
      // Use addCollection to add the fetched collection
      get().addCollection(collection);
      
      // Update collectionIds and loading state
      set(state => ({
        isLoading: { ...state.isLoading, fetch: false }
      }));

      return collection;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, fetch: error.message || 'Failed to fetch collection' },
        isLoading: { ...state.isLoading, fetch: false }
      }));
      throw error;
    }
  },

  createCollection: async (title, icon = 'folder', customInstructions = '', defaults = undefined, parentId = null) => {
    try {      
      set(state => ({ 
        isLoading: { ...state.isLoading, create: true },
        errors: { ...state.errors, create: null }
      }));
      
      const newCollection = await collectionsApi.create({
        title,
        icon,
        customInstructions,
        defaults,
        parentId,
      });

      // Use addCollection to add the new collection
      get().addCollection(newCollection);

      set(state => ({
        isLoading: { ...state.isLoading, create: false }
      }));

      return newCollection;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, create: error.message || 'Failed to create collection' },
        isLoading: { ...state.isLoading, create: false }
      }));
      throw error;
    }
  },

  updateCollection: async (id, updates) => {
    // Store previous state for rollback
    const previousCollection = get().collections[id];
    
    // Optimistic update: immediately update the store
    if (previousCollection) {
      const optimisticCollection = {
        ...previousCollection,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      get().addCollection(optimisticCollection);
    }
    
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, update: true },
        errors: { ...state.errors, update: null }
      }));
      
      const updatedCollection = await collectionsApi.update(id, updates);
      
      // Merge the server response over the previous state. The server is
      // authoritative — re-applying `updates` on top would mask values the
      // backend rejected or normalized (e.g. an invalid icon).
      const mergedCollection = {
        ...previousCollection,
        ...updatedCollection,
      };
      get().addCollection(mergedCollection);
      
      set(state => ({
        isLoading: { ...state.isLoading, update: false }
      }));

      return mergedCollection;
    } catch (error) {
      // Rollback on error
      if (previousCollection) {
        get().addCollection(previousCollection);
      }
      set(state => ({ 
        errors: { ...state.errors, update: error.message || 'Failed to update collection' },
        isLoading: { ...state.isLoading, update: false }
      }));
      throw error;
    }
  },

  renameCollection: (id, newTitle) => {
    return get().updateCollection(id, { title: newTitle });
  },

  // Add conversation to collection locally (without API call)
  addConversationToCollectionLocally: (collectionId, conversationId) => {
    
    // Check all existing collections to see if this conversation is in another collection
    const currentState = get();
    const collectionsToUpdate = [];
    
    Object.values(currentState.collections).forEach(collection => {
      if (collection.id !== collectionId && collection.conversationIds?.includes(conversationId)) {
        // This conversation is in another collection, we need to remove it
        collectionsToUpdate.push({
          ...collection,
          conversationIds: collection.conversationIds.filter(id => id !== conversationId)
        });
      }
    });

    // Get the target collection
    const targetCollection = currentState.collections[collectionId];
    if (targetCollection) {
      // Add the conversation to the target collection
      collectionsToUpdate.push({
        ...targetCollection,
        conversationIds: [...(targetCollection.conversationIds || []), conversationId]
      });
    }

    // Update the chat's inCollectionId in the chat store
    const chatStore = getStores().useChatStore.getState();
    const existingChat = chatStore.chats.find(chat => chat.id === conversationId);
    if (existingChat) {
      chatStore.updateLocalChatDetails(conversationId, { inCollectionId: collectionId });
    }

    // Update all modified collections in one go
    if (collectionsToUpdate.length > 0) {
      get().addCollections(collectionsToUpdate);
    }
  },

  // Add conversation to collection (with API call)
  addConversationToCollection: async (collectionId, conversationId) => {
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, addConversation: true },
        errors: { ...state.errors, addConversation: null }
      }));
      
      // Check all existing collections to see if this conversation is already in another collection
      const currentState = get();
      const collectionsToUpdate = [];
      
      Object.values(currentState.collections).forEach(collection => {
        if (collection.id !== collectionId && collection.conversationIds?.includes(conversationId)) {
          // This conversation is in another collection, we need to remove it
          collectionsToUpdate.push({
            ...collection,
            conversationIds: collection.conversationIds.filter(id => id !== conversationId)
          });
        }
      });

      // Update any collections that had this conversation removed
      if (collectionsToUpdate.length > 0) {
        get().addCollections(collectionsToUpdate);
      }
      
      const updatedCollection = await collectionsApi.addConversation(collectionId, conversationId);
      
      // Update the chat's inCollectionId in the chat store
      const chatStore = getStores().useChatStore.getState();
      const existingChat = chatStore.chats.find(chat => chat.id === conversationId);
      if (existingChat) {
        chatStore.updateLocalChatDetails(conversationId, { inCollectionId: collectionId });
      }
      
      // Use addCollection to update the collection
      get().addCollection(updatedCollection);
      
      set(state => ({
        isLoading: { ...state.isLoading, addConversation: false }
      }));

      return updatedCollection;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, addConversation: error.message || 'Failed to add conversation to collection' },
        isLoading: { ...state.isLoading, addConversation: false }
      }));
      throw error;
    }
  },

  // Remove conversation from collection
  removeConversationFromCollection: async (collectionId, conversationId) => {
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, removeConversation: true },
        errors: { ...state.errors, removeConversation: null }
      }));
      
      const updatedCollection = await collectionsApi.removeConversation(collectionId, conversationId);
      
      // Update the chat's inCollectionId in the chat store (set to null when removed)
      const chatStore = getStores().useChatStore.getState();
      const existingChat = chatStore.chats.find(chat => chat.id === conversationId);
      if (existingChat) {
        chatStore.updateLocalChatDetails(conversationId, { inCollectionId: null });
      }
      
      // Use addCollection to update the collection
      get().addCollection(updatedCollection);
      
      set(state => ({
        isLoading: { ...state.isLoading, removeConversation: false }
      }));

      return updatedCollection;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, removeConversation: error.message || 'Failed to remove conversation from collection' },
        isLoading: { ...state.isLoading, removeConversation: false }
      }));
      throw error;
    }
  },

  // Add files to collection
  addFilesToCollection: async (collectionId, fileIds) => {
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, addFiles: true },
        errors: { ...state.errors, addFiles: null }
      }));
      
      const updatedCollection = await collectionsApi.addFiles(collectionId, fileIds);
      
      // Use addCollection to update the collection
      get().addCollection(updatedCollection);
      
      set(state => ({
        isLoading: { ...state.isLoading, addFiles: false }
      }));

      return updatedCollection;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, addFiles: error.message || 'Failed to add files to collection' },
        isLoading: { ...state.isLoading, addFiles: false }
      }));
      throw error;
    }
  },

  // Remove file from collection
  removeFileFromCollection: async (collectionId, fileId) => {
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, removeFile: true },
        errors: { ...state.errors, removeFile: null }
      }));
      
      const updatedCollection = await collectionsApi.removeFile(collectionId, fileId);
      
      // Use addCollection to update the collection
      get().addCollection(updatedCollection);
      
      set(state => ({
        isLoading: { ...state.isLoading, removeFile: false }
      }));

      return updatedCollection;
    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, removeFile: error.message || 'Failed to remove file from collection' },
        isLoading: { ...state.isLoading, removeFile: false }
      }));
      throw error;
    }
  },

  deleteCollection: async (collectionId) => {
    try {
      set(state => ({ 
        isLoading: { ...state.isLoading, delete: true },
        errors: { ...state.errors, delete: null }
      }));

      // Capture the chat ids before removal — the backend deletes the
      // collection's conversations too, so they must also leave the chat
      // store or stale entries linger in the sidebar until a full refresh.
      const deletedCollection = get().collections[collectionId];
      const deletedConversationIds = new Set(deletedCollection?.conversationIds || []);
      const nextParentId = deletedCollection?.parentId || null;

      await collectionsApi.delete(collectionId);
      
      set(state => {
        const remaining = Object.fromEntries(
          Object.entries(state.collections).filter(([id]) => id !== collectionId)
        );
        Object.values(remaining).forEach((col) => {
          if (col.parentId === collectionId) {
            remaining[col.id] = { ...col, parentId: nextParentId };
          }
        });
        return {
          collections: remaining,
          collectionIds: state.collectionIds.filter(id => id !== collectionId),
          contentSortOrders: Object.fromEntries(
            Object.entries(state.contentSortOrders).filter(([id]) => id !== collectionId)
          ),
          isLoading: { ...state.isLoading, delete: false }
        };
      });

      if (deletedConversationIds.size > 0) {
        getStores().useChatStore.setState(state => ({
          chats: state.chats.filter(chat => !deletedConversationIds.has(chat.id))
        }));
      }

    } catch (error) {
      set(state => ({ 
        errors: { ...state.errors, delete: error.message || 'Failed to delete collection' },
        isLoading: { ...state.isLoading, delete: false }
      }));
      throw error;
    }
  },

  setInitialCollections: (collections) => {    
    // Clear existing collections first
    set({ 
      collections: {},
      collectionIds: []
    });

    // Use addCollections to add the initial collections
    get().addCollections(collections);

    // Set pagination
    set(state => ({
      pagination: {
        page: 1,
        pages: Math.ceil(collections.length / 20),
        total: collections.length,
        perPage: 20,
        hasMore: false
      }
    }));

  },
  
  addCollection: (collection) => {
    const result = get().addCollections([collection]);
    return result;
  },

  addCollections: (newCollections) => {
    if (!Array.isArray(newCollections)) {
      newCollections = [newCollections];
    }

    // Deduplicate collections by ID
    const uniqueCollectionsMap = {};
    const newCollectionIds = [];

    // Extract and store all conversations first
    let conversations = [];
    newCollections.forEach((collection) => {
      if (collection.conversations?.length > 0) {
        conversations = conversations.concat(collection.conversations);
      }
    });
    if (conversations.length > 0) {
      getStores().useChatStore.getState().addChats(conversations);
    }
    
    // Get current state to merge with existing collections
    const currentState = get();
    
    newCollections.forEach(collection => {
      if (collection && collection.id) {
        // Get existing collection if it exists
        const existingCollection = currentState.collections[collection.id] || {};
        
        let conversationIds = [];
        if (collection.conversationIds) {
          conversationIds = collection.conversationIds;
        } else if (collection.conversations?.length > 0) {
          conversationIds = collection.conversations.map(c => c.id);
        } else if (existingCollection.conversationIds) {
          // Preserve existing conversationIds if not provided
          conversationIds = existingCollection.conversationIds;
        }
        
        // Upsert: merge existing collection with new data, preferring new data when provided
        uniqueCollectionsMap[collection.id] = {
          ...existingCollection,
          id: collection.id,
          title: collection.title !== undefined ? collection.title : existingCollection.title,
          files: collection.files !== undefined ? (collection.files || []) : (existingCollection.files || []),
          icon: collection.icon !== undefined ? collection.icon : existingCollection.icon,
          parentId: collection.parentId !== undefined ? (collection.parentId || null) : (existingCollection.parentId || null),
          customInstructions: collection.customInstructions !== undefined ? collection.customInstructions : existingCollection.customInstructions,
          memoryEnabled: collection.memoryEnabled !== undefined ? collection.memoryEnabled : existingCollection.memoryEnabled,
          memoryMode: collection.memoryMode !== undefined ? collection.memoryMode : existingCollection.memoryMode,
          defaults: collection.defaults !== undefined ? collection.defaults : existingCollection.defaults,
          conversationIds: conversationIds,
          createdAt: collection.createdAt !== undefined ? collection.createdAt : existingCollection.createdAt,
          updatedAt: collection.updatedAt !== undefined ? collection.updatedAt : existingCollection.updatedAt
        };
        newCollectionIds.push(collection.id);
      }
    });

    set(state => ({
      collections: { ...state.collections, ...uniqueCollectionsMap },
      collectionIds: [...new Set([...state.collectionIds, ...newCollectionIds])]
    }));

    return Object.values(uniqueCollectionsMap);
  },

  clearCollections: () => {
    set({
      collections: {},
      collectionIds: [],
      isLoading: {
        fetch: false,
        create: false,
        update: false,
        addConversation: false,
        removeConversation: false,
        addFiles: false,
        removeFile: false,
        delete: false
      },
      errors: {
        fetch: null,
        create: null,
        update: null,
        addConversation: null,
        removeConversation: null,
        addFiles: null,
        removeFile: null,
        delete: null
      },
      sortOrder: 'newest',
      contentSortOrders: {},
      pagination: {
        page: 1,
        pages: 1,
        total: 0,
        perPage: 20,
        hasMore: false
      }
    });
  },

  // Helper functions
  getCollectionById: (id) => {
    return get().collections[id];
  },

  getAllCollections: () => {
    const state = get();
    return state.collectionIds.map(id => state.collections[id]);
  },

  getDeepCollectionById: (id) => {
    const collection = get().collections[id];
    if (!collection) return null;

    // Get chats from chat store
    const chats = getStores().useChatStore.getState().chats;
    
    // Get and sort collection chats
    const getLastActivityDate = (chat) => {
      const lastMsg = chat.messages?.[chat.messages.length - 1];
      return new Date(lastMsg?.timestamp || chat.updatedAt || chat.createdAt || 0);
    };
    const collectionChats = collection.conversationIds
      ? collection.conversationIds
          .map(conversationId => chats.find(chat => chat.id === conversationId))
          .filter(Boolean)
          .sort((a, b) => getLastActivityDate(b) - getLastActivityDate(a))
      : [];

    // Return collection with sorted chats
    return {
      ...collection,
      chats: collectionChats,
      chatCount: collectionChats.length,
      lastMessageTime: collectionChats[0]?.lastMessageTime || collectionChats[0]?.createdAt
    };
  },
}),
  {
    name: 'collection-storage',
    storage: createJSONStorage(() => getStorage()),
    partialize: (state) => ({
      collections: state.collections,
      collectionIds: state.collectionIds,
      pagination: state.pagination,
      sortOrder: state.sortOrder,
      contentSortOrders: state.contentSortOrders,
    }),
    onRehydrateStorage: () => () => {
      // Deferred: see getStores().useAuthStore.onRehydrateStorage — sync web hydration
      // runs this during create(), before the store const is initialized.
      queueMicrotask(() => {
        useCollectionStore.setState({ _hasHydrated: true });
      });
    },
  }
));

export { useCollectionStore };
export default useCollectionStore;
